import { useEffect, useMemo, useRef, useState } from "react";
import Icon from "./Icon";

// Lightweight searchable dropdown. options: [{ id, label, hint? }].
// `value` is the option id (or null/"" for none). Renders a small popover
// list that filters as the user types. Closes on outside click or Esc.
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  width = 220,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => String(o.id) === String(value)) || null,
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay = `${o.label || ""} ${o.hint || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    inputRef.current?.focus();
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: "relative", width }}>
      <button
        type="button"
        className="select"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: 6,
          paddingRight: 10,
          // `.select` paints its own chevron via background-image; we render an
          // Icon instead so the caret sits after the clear-X. Without this both
          // draw and the trigger shows two chevrons. Matches MultiSelect.
          backgroundImage: "none",
          color: selected ? "var(--ink-900)" : "var(--ink-500)",
        }}
      >
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? selected.label : placeholder}
        </span>
        {selected && (
          <span
            role="button"
            tabIndex={0}
            title="Clear"
            onClick={(e) => { e.stopPropagation(); onChange(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onChange(""); } }}
            style={{ color: "var(--ink-400)", display: "inline-flex" }}
          >
            <Icon name="x" size={12} />
          </span>
        )}
        <Icon name="chevron-down" size={12} style={{ color: "var(--ink-400)" }} />
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            width: "100%",
            minWidth: "min(220px, calc(100vw - 32px))",
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 7,
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
            zIndex: 100,
            display: "flex",
            flexDirection: "column",
            maxHeight: 300,
          }}
        >
          <div style={{ padding: 6, borderBottom: "1px solid var(--border)", position: "relative" }}>
            <Icon
              name="search"
              size={12}
              style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }}
            />
            <input
              ref={inputRef}
              className="input"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ paddingLeft: 28, fontSize: 12, height: 30 }}
            />
          </div>
          <div style={{ overflowY: "auto", maxHeight: 240 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 14, fontSize: 12, color: "var(--ink-500)", textAlign: "center" }}>
                No matches
              </div>
            ) : (
              filtered.map((o) => {
                const active = String(o.id) === String(value);
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => { onChange(o.id); setOpen(false); setQuery(""); }}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "8px 12px",
                      fontSize: 13,
                      background: active ? "var(--primary-50)" : "transparent",
                      color: active ? "var(--primary-800)" : "var(--ink-800)",
                      borderTop: "1px solid var(--border)",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {o.label}
                    </span>
                    {o.hint && (
                      <span className="t-muted text-xs" style={{ flexShrink: 0 }}>{o.hint}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
